import { Component } from '@angular/core';
import { MatLegacySlideToggleChange } from '@angular/material/legacy-slide-toggle';
import { ButtonSizes, ButtonTypes } from '@app/app/shared/styleguide/components/buttons/default/button.component';
import { ArrowLeft01Icon, ArrowRight01Icon, Search01Icon } from '@hugeicons/core-free-icons/index';
import { LucideChevronLeft, LucideChevronRight, LucideSearch } from '@lucide/angular';

@Component({
  selector: 'ptl-styleguide-demo',
  templateUrl: './styleguide-demo.component.html',
  styleUrls: ['./styleguide-demo.component.scss'],
})
export class StyleguideDemoComponent {
  iconSearchTerm = '';

  // SVG icons available in sprite
  svgIcons = [
    'add-a-photo',
    'add-circle',
    'advanced',
    'announcement',
    'arrow-left',
    'arrow-right',
    'badge',
    'calendar',
    'check',
    'chevron-down',
    'chevron-left',
    'chevron-right',
    'chevron-up',
    'close',
    'comment',
    'copy',
    'download',
    'description',
    'drag-indicator',
    'edit',
    'explore',
    'external-link',
    'history',
    'language',
    'lock',
    'log',
    'mail',
    'menu',
    'moment',
    'notifications',
    'open-in-new',
    'organisation',
    'person',
    'print',
    'profile',
    'rate-review',
    'remove',
    'save',
    'save-active',
    'search',
    'share',
    'settings',
    'tag',
    'thumb-up',
    'thumb-up-active',
    'time',
    'time-loading',
    'tune',
    'upload',
    'file-upload',
    'visibility',
    'visibility-off',
    'warning',
    'logout',
    'unfold-less',
    'unfold-more',
    'more',
    'separator',
    'image',
    'play-circle',
    'file',
    'audio',
    'embed-code',
    'link',
    'text-box',
    'check-box',
    'chart',
    'location',
    'visible-lock',
    'text',
    'align-center',
    'align-full-width',
    'align-left',
    'align-right',
    'arrow-down',
    'arrow-top-down',
    'arrow-up',
    'caption',
    'delete',
    'drag-drop',
    'expand-corner',
    'filter-list',
    'format-align-center',
    'format-align-left',
    'format-align-right',
    'format-bold',
    'format-clear',
    'format-italic',
    'format-bullet-list',
    'filter-number-list',
    'format-quote',
    'framework',
    'format-color',
    'magic',
    'page',
    'multiple-page',
    'pause',
    'play',
    'plus',
    'minus',
    'school',
    'small-image',
    'qr-code',
    'quiz',
    'refresh',
    'teams',
    'toggle-on',
    'unfold-less-horizontal',
    'unfold-more-horizontal',
    'fullscreen-expand',
    'fullscreen-collapse',
    'remove-circle',
    'filter_list',
    'ellipse_6',
    'expand_content',
    'facebook',
    'instagram',
    'linkedin',
    'twitter',
    'twitterbold',
    'pinterest',
    'progress-circle',
    'chat',
    'hashtag',
    'tickets',
    'style',
    'table',
    'attachment',
    'send',
    'user',
    'zoom-in',
    'zoom-out',
    'rotate',
    'flip-vertical',
    'flip-horizontal',
    'earth',
    'ship',
    'bricks',
    'home',
    'message',
    'home-flat',
    'compass',
    'open',
    'process-flow',
    'robot',
    'alarm',
    'review-note',
    'revert',
    'playlist-card',
    'box',
    'edit-square',
    'permission',
    'users-check',
    'trash',
    'archive',
    'briefcase',
    'folio',
    'swirly-arrow',
    'bricks-registry',
    'checkmark',
    'important',
    'circle-chart',
    'verified',
    'eye',
    'eye-hide',
    'facebook-square',
    'linkedin-square',
    'x',
    'layout-list-icon',
    'layout-list',
    'activity',
    'attach',
    'camera',
    'link-rel',
    'microphone',
    'heart',
    'lighting',
    'sort-to-bottom',
    'hand-with-key',
    'repeat',
    'home_2',
    'message_2',
    'user_2',
    'thinking',
    'join',
    'bell-curve',
    'location-2',
    'path',
    'bell-curve-2',
    'plan',
    'megaphone',
  ];

  toggleEnabled = true;
  toggleDisabled = false;
  buttonTypes: ButtonTypes[] = [
    'primary',
    'secondary',
    'secondary-neutral',
    'tertiary-neutral',
    'tertiary-color',
    'link-neutral',
    'link-color',
  ];

  buttonSizes: ButtonSizes[] = ['xs', 's', 'm', 'l', 'xl'];
  eventType = 'ONLINE';
  radioOptions = [
    { value: 'ONLINE', label: 'Online Event' },
    { value: 'VENUE', label: 'Venue Event' },
  ];

  onButtonClick(buttonName: string): void {
    console.log(`${buttonName} button clicked`);
  }

  onToggleChange(event: MatLegacySlideToggleChange): void {
    console.log('Toggle changed:', event.checked);
  }

  onRadioChange(value: string): void {
    console.log('Radio changed:', value);
    this.eventType = value;
  }

  getFilteredIcons(): string[] {
    if (!this.iconSearchTerm) {
      return this.svgIcons;
    }
    return this.svgIcons.filter((icon) => icon.toLowerCase().includes(this.iconSearchTerm.toLowerCase()));
  }

  isIconHighlighted(icon: string): boolean {
    return this.iconSearchTerm && icon.toLowerCase().includes(this.iconSearchTerm.toLowerCase());
  }

  copyToClipboard(elementType: string): void {
    const htmlMap = {
      flatButtonPrimary: '<ptl-sg-button type="flat" color="primary" text="Primary"></ptl-sg-button>',
      flatButtonAccent: '<ptl-sg-button type="flat" color="accent" text="Accent"></ptl-sg-button>',
      flatButtonWarn: '<ptl-sg-button type="flat" color="warn" text="Warning"></ptl-sg-button>',
      strokedButtonPrimary: '<ptl-sg-button type="stroked" color="primary" text="Primary"></ptl-sg-button>',
      strokedButtonAccent: '<ptl-sg-button type="stroked" color="accent" text="Accent"></ptl-sg-button>',
      strokedButtonWarn: '<ptl-sg-button type="stroked" color="warn" text="Warning"></ptl-sg-button>',
      flatButtonWithIcon: '<ptl-sg-button type="flat" color="primary" matIconName="save" text="Save"></ptl-sg-button>',
      strokedButtonWithIcon: '<ptl-sg-button type="stroked" color="accent" matIconName="edit" text="Edit"></ptl-sg-button>',
      flatButtonDelete: '<ptl-sg-button type="flat" color="warn" matIconName="delete" text="Delete"></ptl-sg-button>',
      iconButtonPrimary: '<ptl-sg-button type="icon" color="primary" matIconName="edit"></ptl-sg-button>',
      iconButtonAccent: '<ptl-sg-button type="icon" color="accent" matIconName="settings"></ptl-sg-button>',
      iconButtonWarn: '<ptl-sg-button type="icon" color="warn" matIconName="delete"></ptl-sg-button>',
      iconButtonDisabled: '<ptl-sg-button type="icon" [disabled]="true" matIconName="close"></ptl-sg-button>',
      inputWithSearchIcon: '<ptl-sg-input [withIcon]="true" iconName="search" placeholder="Search content"></ptl-sg-input>',
      inputSearchByName: '<ptl-sg-input [withIcon]="false" iconName="search" placeholder="Search by name"></ptl-sg-input>',
      toggleEnabled: '<ptl-sg-toggle [checked]="true">Enable tickets</ptl-sg-toggle>',
      toggleDisabled: '<ptl-sg-toggle [disabled]="true">Disabled toggle</ptl-sg-toggle>',
      toggleWaitingList: '<ptl-sg-toggle [waitingList]="true" size="small" [checked]="false">Add extra to waiting list</ptl-sg-toggle>',
      radioGroupEventType:
        '<ptl-sg-radio-group [options]="radioOptions" [(ngModel)]="eventType" name="eventTypeSelector"></ptl-sg-radio-group>',
      // SVG Sprite Icon Buttons
      flatButtonSvgSave: '<ptl-sg-button type="flat" color="primary" spriteSvgName="save" text="Save Document"></ptl-sg-button>',
      strokedButtonSvgEdit: '<ptl-sg-button type="stroked" color="accent" spriteSvgName="edit" text="Edit Content"></ptl-sg-button>',
      iconButtonSvgProfile: '<ptl-sg-button type="icon" color="primary" spriteSvgName="profile"></ptl-sg-button>',
      iconButtonSvgSettings: '<ptl-sg-button type="icon" color="accent" spriteSvgName="settings"></ptl-sg-button>',
      flatButtonSvgDelete: '<ptl-sg-button type="flat" color="warn" spriteSvgName="trash" text="Delete"></ptl-sg-button>',
      strokedButtonSvgDownload:
        '<ptl-sg-button type="stroked" color="primary" spriteSvgName="download" text="Download File"></ptl-sg-button>',
      // v3-styleguide Typography CSS classes - Nested format (preferred)
      headline1: '<h1 class="headline1">Your heading text</h1>',
      headline2: '<h2 class="headline2">Your heading text</h2>',
      headline3: '<h3 class="headline3">Your heading text</h3>',
      headline4: '<h4 class="headline4">Your heading text</h4>',
      headline5: '<h5 class="headline5">Your heading text</h5>',
      headline6: '<h6 class="headline6">Your heading text</h6>',

      // Caption styles
      'caption-semibold': '<p class="caption semibold">Your caption text</p>',
      'caption-medium': '<p class="caption">Your caption text</p>',

      // Body styles - nested format (base class + modifier)
      'body-l-medium': '<p class="body-l">Your body text</p>',
      'body-l-semibold': '<p class="body-l semibold">Your body text</p>',
      'body-l-link': '<p class="body-l link">Your link text</p>',
      'body-m-medium': '<p class="body-m ">Your body text</p>',
      'body-m-semibold': '<p class="body-m semibold">Your body text</p>',
      'body-m-link': '<p class="body-m link">Your link text</p>',
      'body-s-medium': '<p class="body-s">Your body text</p>',
      'body-s-semibold': '<p class="body-s bold">Your body text</p>',
      'body-s-link': '<p class="body-s link">Your link text</p>',
      'body-xs-medium': '<p class="body-xs">Your body text</p>',
      'body-xs-semibold': '<p class="body-xs semibold">Your body text</p>',
      'body-xs-link': '<p class="body-xs link">Your link text</p>',

      overline: '<p class="overline">Your overline text</p>',

      // V3 Button Types
      buttonType_PRIMARY:
        '<ptl-button type="primary" size="m" [icon]="\'arrow_back_ios\'" [iconSuffix]="\'arrow_forward_ios\'" text="Button" ariaLabel="Button action"></ptl-button>',
      buttonType_SECONDARY:
        '<ptl-button type="secondary" size="m" [icon]="\'arrow_back_ios\'" [iconSuffix]="\'arrow_forward_ios\'" text="Button" ariaLabel="Button action"></ptl-button>',
      buttonType_SECONDARY_NEUTRAL:
        '<ptl-button type="secondary-neutral" size="m" [icon]="\'arrow_back_ios\'" [iconSuffix]="\'arrow_forward_ios\'" text="Button" ariaLabel="Button action"></ptl-button>',
      buttonType_TERTIARY_NEUTRAL:
        '<ptl-button type="tertiary-neutral" size="m" [icon]="\'arrow_back_ios\'" [iconSuffix]="\'arrow_forward_ios\'" text="Button" ariaLabel="Button action"></ptl-button>',
      buttonType_TERTIARY_COLOR:
        '<ptl-button type="tertiary-color" size="m" [icon]="\'arrow_back_ios\'" [iconSuffix]="\'arrow_forward_ios\'" text="Button" ariaLabel="Button action"></ptl-button>',
      buttonType_LINK_NEUTRAL:
        '<ptl-button type="link-neutral" size="m" [icon]="\'arrow_back_ios\'" [iconSuffix]="\'arrow_forward_ios\'" text="Button" ariaLabel="Button action"></ptl-button>',
      buttonType_LINK_COLOR:
        '<ptl-button type="link-color" size="m" [icon]="\'arrow_back_ios\'" [iconSuffix]="\'arrow_forward_ios\'" text="Button" ariaLabel="Button action"></ptl-button>',
    };

    if (elementType.startsWith('svgIcon_')) {
      const iconName = elementType.replace('svgIcon_', '');
      const svgHtml = `<svg class="svg-icon" aria-hidden="true">
      <use ogSvgUnify="assets/sprite/sprite.svg#${iconName}"></use>
    </svg>`;
      navigator.clipboard
        .writeText(svgHtml)
        .then(() => {
          console.log(`Copied ${iconName} SVG to clipboard`);
        })
        .catch((err) => {
          console.error('Could not copy text: ', err);
        });
      return;
    }

    const html = htmlMap[elementType as keyof typeof htmlMap];
    if (html) {
      navigator.clipboard
        .writeText(html)
        .then(() => {
          console.log(`Copied ${elementType} HTML to clipboard`);
        })
        .catch((err) => {
          console.error('Could not copy text: ', err);
        });
    }
  }

  protected readonly ArrowLeft01Icon = ArrowLeft01Icon;
  protected readonly ArrowRight01Icon = ArrowRight01Icon;
  protected readonly Search01Icon = Search01Icon;
  protected readonly LucideSearch = LucideSearch;
  protected readonly LucideChevronLeft = LucideChevronLeft;
  protected readonly LucideChevronRight = LucideChevronRight;
}
